import React, { useEffect } from "react";

import {
  Control,
  UseFormHandleSubmit,
  Controller,
  useFormContext,
} from "react-hook-form";

import { Button, ErrorUI, SVGSprite } from "@/components/Atoms";

import EmailInputV2 from "@/components/Organisms/FormInputsV2/EmailInput";
import NameInputV2 from "@/components/Organisms/FormInputsV2/NameInput";
import PhoneInputV2 from "@/components/Organisms/FormInputsV2/PhoneInput";
import CityInputV2 from "@/components/Organisms/FormInputsV2/CityInput";
import SelectPackagesV2 from "@/components/Organisms/FormInputsV2/SelectPackages";
import DateInputV2 from "@/components/Organisms/FormInputsV2/DateInput";

import { FormInputsI, PackagesI } from "./formMap";
import { FormValuesI } from "./useRegisterForm";

import styles from "./FormInputs.module.scss";
import Typography from "@/foundation/Typography/V2";
import OfferCounter from "../OfferCounter";
import SelectInput from "@/components/Atoms/Inputs/Select";

interface FormInputsProps extends FormInputsI {
  isLoading: boolean;
  control: Control<FormValuesI>;
  handleSubmit: UseFormHandleSubmit<FormValuesI>;
  packages?: PackagesI[];
  selectedPackage: PackagesI;
  handleBlur: () => void;
  onSubmitCallback: (state: boolean) => void;
  purchasePlanButton?: any;
  hideSinglePurchaseOption?: boolean;
  isShyftPassRenewalPage?: boolean;
  bookingError?: string;
  offerEndDate?: string;
  offerTextFirstPart?: string;
  offerTextSecondPart?: string;
  showOfferCounter?: boolean;
  shyftPassZoneList?: { name: string; id: number }[];
  isSingleClassPurchasePage?: boolean;
}

const FormInputs: React.FC<FormInputsProps> = ({
  isLoading,
  control,
  handleSubmit,
  showNameField,
  namePlaceholder,
  showPhoneField,
  phonePlaceholder,
  showEmailField,
  emailPlaceholder,
  showCityField,
  cityPlaceholder,
  showPackages,
  packagePlaceholder,
  info,
  showRequestCallback,
  packages,
  selectedPackage,
  showStartDateField,
  handleBlur = () => {},
  onSubmitCallback,
  purchasePlanButton,
  hideSinglePurchaseOption,
  isShyftPassRenewalPage,
  bookingError,
  offerEndDate,
  offerTextFirstPart,
  offerTextSecondPart,
  showOfferCounter,
  shyftPassZoneList,
  isSingleClassPurchasePage,
}) => {
  useEffect(() => {
    const nameInput = document.querySelector("input[name]");
    nameInput.focus();
    nameInput.blur();
  }, []);

  const nameField = showNameField && (
    <NameInputV2
      className="className"
      name="name"
      control={control}
      namePlaceholder={namePlaceholder}
      isNotAbsolute
    />
  );

  const phoneField = showPhoneField && (
    <PhoneInputV2
      name="mobile"
      control={control}
      mobilePlaceholder={phonePlaceholder}
      handleBlur={handleBlur}
      isNotAbsolute
    />
  );

  const emailField = showEmailField && (
    <EmailInputV2
      name="email"
      control={control}
      emailPlaceholder={emailPlaceholder}
      handleBlur={handleBlur}
      isNotAbsolute
    />
  );

  const cityField = showCityField && (
    <CityInputV2
      name="city"
      control={control}
      namePlaceholder={cityPlaceholder}
      handleBlur={handleBlur}
      isNotAbsolute
    />
  );

  const packagesField = showPackages && packages?.length > 0 && (
    <SelectPackagesV2
      name="packageSlug"
      control={control}
      packages={packages}
      packagePlaceholder={packagePlaceholder}
      handleBlur={handleBlur}
    />
  );

  const startDateField = showStartDateField && (
    <div className={styles.startDateWrapper}>
      <div className={styles.startDateLabel}>
        Select a start date for your membership
      </div>
      <div className={styles.startDateInput}>
        <DateInputV2
          name="startDate"
          control={control}
          handleBlur={handleBlur}
          minDate={new Date()}
        />
      </div>
    </div>
  );

  const purchasePlanField = showPackages && packages?.length > 0 && (
    <div className={styles.purchasePlanWrapper}>
      <div className={styles.purchasePlan}>
        <Button
          isLoading={isLoading}
          className={[
            styles.button,
            isShyftPassRenewalPage ? styles.renew : "",
          ].join(" ")}
          type="submit"
        >
          <span>{isShyftPassRenewalPage ? "Renew Shyft pass" : "Buy now"}</span>
        </Button>
        <div className={styles.pricing}>
          {/* <div
          className={styles.currency}
          dangerouslySetInnerHTML={{
            __html: selectedPackage?.currencyCode,
          }}
        /> */}
          <div
            className={styles.price}
            dangerouslySetInnerHTML={{
              __html: `${selectedPackage?.currencySymbol} ${selectedPackage?.price}`,
            }}
          />
        </div>
      </div>
      {!hideSinglePurchaseOption && (
        <div className={styles.singlePlan}>
          <Typography variant="routelink2" to="/shyftpass/single-class">
            or book a slot for single class
            <SVGSprite
              icon="icon-arrow-right-shyftpass"
              height="16px"
              width="16px"
              style={{ marginLeft: "2px" }}
            />
          </Typography>
        </div>
      )}
    </div>
  );

  const errorUI = bookingError && (
    <ErrorUI
      hasBottomBorder
      error={bookingError}
      isNotAbsolute
      style={{ margin: "16px 0" }}
    />
  );

  const infoText = info && (
    <div
      className={[styles.infoText, "info"].join(" ")}
      dangerouslySetInnerHTML={{ __html: info }}
    />
  );

  const requestCallbackField = showRequestCallback && (
    <div className={styles.requestCallback}>
      Need more information?
      <a onClick={handleSubmit(() => onSubmitCallback(true))}>
        Request a callback
      </a>
    </div>
  );
  const shyftPassZoneOption = shyftPassZoneList?.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const selectZoneDropdown = (
    <div className={styles.zoneDropdown}>
      <Controller
        name={"zoneId"}
        control={control}
        shouldUnregister
        render={({
          field: { value, onBlur, onChange, ref },
          fieldState: { error },
        }) => (
          <div className={styles.zoneDropdownContainer}>
            <label htmlFor="shyftPassZone">Select your preferred zone</label>
            <SelectInput
              options={shyftPassZoneOption}
              dropDownHeight={"150px"}
              placeholder={"Ex: Gurgaon"}
              name="shyftPassZone"
              value={shyftPassZoneOption?.find((op) => op.value === value)}
              onChange={({ value }) => onChange(value)}
              containerPadding={"8px 24px"}
              // menuIsOpen
              dropdownPos={"bottom"}
              variant={"v3"}
              isSearchable={false}
              fontSize="14px"
              color="#282828"
              minHeight="48px"
              classNamePrefix="react-select"
              singleValueColor="#282828"
              fontFamily="var(--dm-sans)"
              caretIcon="icon-arrow-down"
              style={{ border: "none", height: "46px" }}
            />
            {error && (
              <ErrorUI
                hasBottomBorder
                error={error?.message}
                isNotAbsolute
                style={{ margin: "4px  0px 0px 8px" }}
              />
            )}
          </div>
        )}
      />
    </div>
  );

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit(() => onSubmitCallback(false))}
    >
      {nameField}
      <div
        className={[
          styles.nameAndEmailContainer,
          isSingleClassPurchasePage ? styles.nameAndEmailContainerColumn : "",
        ].join(" ")}
      >
        {phoneField}
        {emailField}
      </div>
      {!isSingleClassPurchasePage && selectZoneDropdown}
      {/* {cityField} */}
      {/* {infoText} */}
      {packagesField}
      {showOfferCounter && (
        <OfferCounter
          offerEndDate={offerEndDate}
          offerTextFirstPart={offerTextFirstPart}
          offerTextSecondPart={offerTextSecondPart}
        />
      )}
      {startDateField}
      {purchasePlanField}
      {errorUI}
      {purchasePlanButton}
      {requestCallbackField}
    </form>
  );
};

export default FormInputs;
